import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSpeakerNotePages } from "../../../queries/speakerNotes";
import MarkdownEditor from "../../../components/MarkdownEditor";

/**
 * @description 발표자 노트 페이지
 * - 왼쪽: 노트 페이지 목록 (content 없이 가볍게 로드)
 * - 오른쪽: 선택된 페이지의 content (선택 시 로드)
 */
export default function SpeakerNotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfId = searchParams.get("id");

  const {
    pages,
    activePage,
    loading,
    contentLoading,
    selectPage,
    handleContentChange,
    handleTitleChange,
    addPage,
    removePage,
  } = useSpeakerNotePages(pdfId);

  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              {t("dashboard.speaker_notes_title")}
            </h1>
          </div>
        </div>
        {/* 자동저장이므로 저장 버튼 제거 */}
        <span className="text-xs text-gray-400">자동 저장</span>
      </header>

      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* 왼쪽: 페이지 목록 */}
          <div className="lg:w-56 flex-shrink-0">
            <div className="flex items-center justify-between mb-3 px-1">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                페이지
              </p>
              <button
                onClick={addPage}
                className="w-6 h-6 flex items-center justify-center bg-indigo-100 hover:bg-indigo-200 text-indigo-600 rounded transition-all cursor-pointer"
                title="새 페이지 추가"
              >
                <i className="ri-add-line text-sm" />
              </button>
            </div>

            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
              {pages.length === 0 && (
                <p className="text-xs text-gray-400 px-1">
                  + 버튼으로 페이지를 추가하세요
                </p>
              )}
              {pages.map((page) => (
                <div
                  key={page.id}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer flex-shrink-0 ${
                    activePage?.id === page.id
                      ? "bg-indigo-500 text-white shadow-lg"
                      : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                  }`}
                  onClick={() => selectPage(page.id)}
                >
                  {editingTitleId === page.id ? (
                    <input
                      autoFocus
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onBlur={() => {
                        handleTitleChange(page.id, titleDraft || "새 페이지");
                        setEditingTitleId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleTitleChange(page.id, titleDraft || "새 페이지");
                          setEditingTitleId(null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 min-w-0 bg-transparent outline-none text-sm"
                    />
                  ) : (
                    <span
                      className="flex-1 min-w-0 truncate"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setEditingTitleId(page.id);
                        setTitleDraft(page.title);
                      }}
                    >
                      {page.title}
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removePage(page.id);
                    }}
                    className={`opacity-0 group-hover:opacity-100 w-4 h-4 flex items-center justify-center rounded transition-all ${
                      activePage?.id === page.id
                        ? "text-white/70 hover:text-white"
                        : "text-gray-400 hover:text-red-500"
                    }`}
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 오른쪽: 에디터 */}
          <div className="flex-1">
            {contentLoading ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent" />
              </div>
            ) : !activePage ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center h-64">
                <p className="text-sm text-gray-400">페이지를 선택하거나 추가하세요</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    {activePage.title}
                  </span>
                  <span className="text-xs text-gray-400">
                    {activePage.content.length}자
                  </span>
                </div>
                <MarkdownEditor
                  value={activePage.content}
                  onChange={handleContentChange}
                  placeholder={t("dashboard.notes_placeholder")}
                />
              </div>
            )}

            {/* 안내 */}
            <div className="mt-4 bg-indigo-50 rounded-xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-indigo-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-indigo-700 mb-1">
                  {t("dashboard.notes_tip_title")}
                </p>
                <p className="text-xs text-indigo-600/70 leading-relaxed">
                  {t("dashboard.notes_tip_desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
